import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Button,
  Spin,
  Checkbox,
  Tag,
  Card,
  Space,
  Empty,
  message,
  Drawer,
  Badge,
  Tooltip,
} from 'antd';
import {
  UnorderedListOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  StarFilled,
  PlusOutlined,
  RightOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { GoTasklist } from 'react-icons/go';
import { Link } from 'react-router-dom';
import TodoService from '../services';
import TodoForm from '../components/partials/content/TodoForm';
import dayjs from 'dayjs';

const { Text } = Typography;

const MAX_PENDING_DISPLAY = 20;

function getPriorityTagProps(priority) {
  switch (priority) {
    case 'high':
      return { color: 'error', icon: <ExclamationCircleOutlined /> };
    case 'medium':
      return { color: 'warning', icon: <ClockCircleOutlined /> };
    case 'low':
      return { color: 'success', icon: null };
    default:
      return null;
  }
}

function TaskCard({ todo, onComplete }) {
  const [completing, setCompleting] = useState(false);
  const dueDate = todo.due_date ?? todo.dueDate;
  const isOverdue = dueDate && dayjs(dueDate).isBefore(dayjs(), 'day');
  const priorityTag = getPriorityTagProps(todo.priority);

  const handleComplete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (completing) return;
    setCompleting(true);
    try {
      const response = await TodoService.toggleCompleted(todo.id);
      const result = response?.data || response || {};
      if (result.success) {
        onComplete(todo.id);
      } else {
        message.error('Could not update task');
      }
    } catch (err) {
      message.error(err?.message || 'Failed to update task');
    } finally {
      setCompleting(false);
    }
  };

  return (
    <Card size="small" className={`todo-notification-card${isOverdue ? ' todo-notification-card--overdue' : ''}`}>
      <Space orientation="vertical" size="small" className="todo-notification-card__content">
        <Space align="flex-start" size="small" className="todo-notification-card__main">
          <Checkbox
            checked={false}
            disabled={completing}
            onChange={handleComplete}
            onClick={(e) => e.stopPropagation()}
            aria-label="Mark as complete"
          />
          <Space orientation="vertical" size={0} className="todo-notification-card__copy">
            <Text strong className="todo-notification-card__title">
              {todo.title}
            </Text>
            {todo.description && (
              <Text type="secondary" className="todo-notification-card__description">
                {todo.description}
              </Text>
            )}
            <Space size={6} wrap className="todo-notification-card__meta">
              {dueDate && (
                <Tag
                  color={isOverdue ? 'red' : 'secondary'}
                  variant="solid"
                  title={dayjs(dueDate).format('MMM D, YYYY')}
                >
                  <ClockCircleOutlined /> {isOverdue ? 'Overdue ' : ''}
                  {dayjs(dueDate).format('MMM D, YYYY')}
                </Tag>
              )}
              {priorityTag && (
                <Tag color={priorityTag.color}>
                  {priorityTag.icon} {todo.priority}
                </Tag>
              )}
              {todo.starred && (
                <StarFilled style={{ color: 'var(--varexe-warning)' }} title="Starred" />
              )}
              {Array.isArray(todo.tags) &&
                todo.tags.slice(0, 3).map((tag, i) => (
                  <Tag key={i} style={{ margin: 0 }}>
                    {tag}
                  </Tag>
                ))}
              {Array.isArray(todo.tags) && todo.tags.length > 3 && (
                <Text type="secondary">
                  +{todo.tags.length - 3}
                </Text>
              )}
            </Space>
          </Space>
        </Space>
        <div className="todo-notification-card__footer">
          <Link
            to="/todos"
            className="todo-notification-card__link"
          >
            Open task
            <RightOutlined style={{ fontSize: 10 }} />
          </Link>
        </div>
      </Space>
    </Card>
  );
}

function TodoTopNavbarDrawer() {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);

  const loadTodos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await TodoService.getTodos();
      const result = response?.data || response || {};
      if (result.success) {
        const list = Array.isArray(result.data) ? result.data : [];
        const pendingOnly = list.filter((t) => !t.completed);
        const due = (d) => d.due_date ?? d.dueDate;
        const sortByDue = (a, b) => {
          const aDue = due(a);
          const bDue = due(b);
          const aOverdue = aDue && dayjs(aDue).isBefore(dayjs(), 'day');
          const bOverdue = bDue && dayjs(bDue).isBefore(dayjs(), 'day');
          if (aOverdue && !bOverdue) return -1;
          if (!aOverdue && bOverdue) return 1;
          if (aDue && bDue) return dayjs(aDue).valueOf() - dayjs(bDue).valueOf();
          if (aDue) return -1;
          if (bDue) return 1;
          return 0;
        };
        setPending([...pendingOnly].sort(sortByDue));
      } else {
        setPending([]);
      }
    } catch (e) {
      setError(e?.message || 'Failed to load tasks');
      setPending([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTodos();
  }, [loadTodos]);

  useEffect(() => {
    if (open) {
      loadTodos();
    }
  }, [open, loadTodos]);

  const handleComplete = useCallback((id) => {
    setPending((prev) => prev.filter((t) => t.id !== id));
  }, []);

  /*
  | The quick-add drawer renders the SAME TodoForm as the Todos page, but it was not
  | passing the options that form needs — so Members came up with an empty list and the
  | "Relates to" fields were hidden entirely (they only render when relatedTypes has
  | entries). Same component, two different forms, depending on where you opened it.
  |
  | Fetched when the modal opens rather than on mount: most people open this drawer to
  | glance at their tasks, not to add one.
  */
  const [formOptions, setFormOptions] = useState({ assignees: [], related_types: [] });

  useEffect(() => {
    if (!addModalOpen) return;

    let cancelled = false;
    TodoService.getOptions()
      .then((response) => {
        const payload = response?.data?.data;
        if (!cancelled && payload) setFormOptions(payload);
      })
      .catch(() => {
        // The form still works without them — Members is simply empty, which is the
        // behaviour this drawer already had.
      });

    return () => { cancelled = true; };
  }, [addModalOpen]);

  const handleAddTodo = useCallback(
    async (todoData) => {
      setAddSubmitting(true);
      try {
        const response = await TodoService.createTodo(todoData);
        const result = response?.data || response || {};
        if (result.success) {
          message.success('Task added');
          setAddModalOpen(false);
          loadTodos();
        } else {
          message.error('Could not add the task.');
        }
      } catch (err) {
        message.error(err?.response?.data?.message || 'Could not add the task.');
      } finally {
        setAddSubmitting(false);
      }
    },
    [loadTodos]
  );

  const displayList = pending.slice(0, MAX_PENDING_DISPLAY);
  const hasMore = pending.length > MAX_PENDING_DISPLAY;

  const renderBody = () => {
    if (loading) {
      return (
        <div className="todo-notification-drawer__state">
          <Spin size="small" />
          <Text type="secondary" style={{ marginTop: 8 }}>
            Loading pending tasks…
          </Text>
        </div>
      );
    }
    if (error) {
      return (
        <div className="todo-notification-drawer__state todo-notification-drawer__state--error">
          <Text type="secondary" strong style={{ display: 'block', marginBottom: 4 }}>
            Todos
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {error}
          </Text>
        </div>
      );
    }
    if (displayList.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span>
              <Text type="secondary" strong style={{ display: 'block', marginBottom: 4 }}>
                No pending tasks
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Your tasks and reminders will show here.
              </Text>
            </span>
          }
        />
      );
    }
    return (
      <>
        <Space orientation="vertical" size={8} className="todo-notification-drawer__list">
          {displayList.map((todo) => (
            <TaskCard key={todo.id} todo={todo} onComplete={handleComplete} />
          ))}
        </Space>
        {hasMore && (
          <div className="todo-notification-drawer__more">
            <Text type="secondary" style={{ fontSize: 11 }}>
              +{pending.length - MAX_PENDING_DISPLAY} more
            </Text>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <Tooltip title="Todos">
        <Badge count={pending.length} size="small" showZero={false} overflowCount={99} offset={[-2, 2]}>
          <Button
            className="m-0 p-0"
            color="default" variant="filled"
            icon={<GoTasklist className="fs-1" />}
            onClick={() => setOpen(true)}
          />
        </Badge>
      </Tooltip>
      <Drawer
        rootClassName="todo-notification-drawer"
        title={
          <Space>
            <UnorderedListOutlined />
            <span>Todos</span>
            {!loading && !error && (
              <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
                {pending.length} pending
              </Text>
            )}
          </Space>
        }
        placement="right"
        size={400}
        onClose={() => setOpen(false)}
        open={open}
        closable
        keyboard
        styles={{ body: { padding: 0 } }}
      >
        <div className="todo-notification-drawer__toolbar">
          <div>
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
              Add todo
            </Button>
          </div>
          <div>
            <Link className="todo-notification-drawer__open-all" to="/todos" onClick={() => setOpen(false)}>
              Open Todos <ArrowRightOutlined className='fs--2' />
            </Link>
          </div>
        </div>
        <div className="todo-notification-drawer__body">{renderBody()}</div>
      </Drawer>
      <TodoForm
        visible={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        onSubmit={handleAddTodo}
        title="Add task"
        initialValues={null}
        loading={addSubmitting}
        assignees={formOptions.assignees}
        relatedTypes={formOptions.related_types}
      />
    </>
  );
}

export default TodoTopNavbarDrawer;
